import { css } from '@emotion/react';
import { Button, Card, color, spacing, Typography } from '@uniquegood/realworld-studio-design';
import { useState, useEffect, useRef, useMemo } from 'react';
import Flatpickr from 'react-flatpickr';
import { Korean } from 'flatpickr/dist/l10n/ko';
import dayjs from 'dayjs';
import queryString from 'query-string';
import download from 'downloadjs';
import { RowFlex, JustifySpaceBetween, MarginBottom16, MarginRight16 } from '@/styles';
import PeriodSelector from './PeriodSelector';
import TrendChart from './TrendChart';
import { PeriodOptionValue } from '@/config/planLimits';
import '@styles/flatpickr.css';
import { ClickEventName, track } from '@/track';
import { coreApi } from '@/api';
import { useRequest } from '@/hooks';
import LoadingSpinner from '@/components/LoadingSpinner';

interface TrendSectionProps {
  onPeriodChange?: (period: PeriodOptionValue) => void;
  appId?: string;
  projectId: string;
  plan?: PlanType;
  canDownload?: boolean;
}

export default function TrendSection({
  onPeriodChange,
  appId,
  projectId,
  plan = 'Free',
  canDownload = false
}: TrendSectionProps) {
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarWrapperRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout>();
  const { data: applicationInfo, mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(`/apps/${appId}/info`);
  const { data: projectDataResponse, mutate: mutateProjectData } = useRequest<
    CommonResponseModel<ProjectV2>
  >(
    `/apps/${appId}/projects/${projectId}`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );
  const projectData = projectDataResponse?.data;
  const { data: me, mutate: mutateMe } = useRequest<MyInfo>('/api/me');

  // Cleanup: 타이머 정리
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // plan에 따른 기본 period 계산
  const getDefaultPeriod = (planType: PlanType | undefined): PeriodOptionValue => {
    if (!planType || planType === 'Free') return 'week';
    if (planType === 'Pro' || planType === 'Teacher') return 'month';
    if (planType === 'Enterprise' || planType === 'Class' || planType === 'Realworld')
      return 'year';
    return 'week';
  };

  // 기본 날짜 범위 계산
  const defaultDateRange = useMemo(() => getDefaultDateQuery(plan), [plan]);
  const defaultPeriod = useMemo(() => getDefaultPeriod(plan), [plan]);

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOptionValue>(defaultPeriod);
  const [dateRange, setDateRange] =
    useState<{ startDate: string; endDate: string }>(defaultDateRange);

  // Mock data 생성 (서버 문제로 인해 임시 사용)
  // TODO: 서버 에러 해결 후 적용
  const periodQuery = queryString.stringify(dateRange);
  const { data: { data: periodData } = {}, isValidating: isLoading } = useRequest<
    CommonResponseModel<ProjectPeriodStatisticsResponseModel>
  >(`/apps/${appId}/projects/${projectId}/statistics/period?${periodQuery}`);
  // const periodData = useMemo(() => generateMockPeriodStatistics(dateRange), [dateRange]);
  // const isLoading = false;

  const { startDate, endDate } = dateRange;
  const dates = [startDate, endDate];

  const closeCalendar = () => {
    setShowCalendar(false);
  };

  // 기간 선택 핸들러
  const handlePeriodChange = (period: PeriodOptionValue) => {
    setSelectedPeriod(period);
    closeCalendar();

    const { startDate: newStartDate, endDate: newEndDate } = getPeriodDates(period);
    const newDateRange = { startDate: newStartDate, endDate: newEndDate };

    setDateRange(newDateRange);

    // 부모 컴포넌트에 기간 변경 알림
    if (onPeriodChange) {
      onPeriodChange(period);
    }

    if (projectId) {
      track.onClick({
        clickEventName: ClickEventName.click_dashboard_input_daterange,
        params: {
          projectId,
          period
        }
      });
    }
  };

  // 커스텀 날짜 선택 - 캘린더 열기
  const handleDateRangeClick = () => {
    // 닫기 타이머가 있다면 취소
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setSelectedPeriod('custom');
    setShowCalendar(true);

    if (projectId) {
      track.onClick({
        clickEventName: ClickEventName.click_dashboard_input_daterange,
        params: {
          projectId,
          period: 'custom'
        }
      });
    }
  };

  // 캘린더 닫기 (딜레이 적용)
  const handleCalendarClose = () => {
    // 200ms 딜레이 후 닫기 (캘린더로 마우스 이동 시간 확보)
    closeTimeoutRef.current = setTimeout(() => {
      setShowCalendar(false);
    }, 200);
  };

  // 캘린더 영역에 마우스가 들어오면 닫기 취소
  const handleCalendarMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  };

  const handleDownload = async () => {
    if (!appId || !projectId) return;

    const query = queryString.stringify(dateRange);
    const response = await coreApi.get<Blob>(
      `/apps/${appId}/projects/${projectId}/statistics/download?${query}`,
      { responseType: 'blob' }
    );

    // Content-Disposition 헤더에서 파일명 추출
    const contentDisposition = response.headers['content-disposition'];
    const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
      contentDisposition || ''
    );
    const rawFileName = fileNameMatch?.[1] || fileNameMatch?.[2];

    // 파일 내용의 첫 바이트로 형식 감지 (xlsx는 ZIP 형식이라 'PK'로 시작)
    const detectExtension = async (blob: Blob): Promise<string> => {
      const arr = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
      // ZIP/xlsx 시그니처: 0x50 0x4B (PK)
      if (arr[0] === 0x50 && arr[1] === 0x4b) return 'xlsx';
      return 'csv';
    };

    // 서버에서 파일명을 주면 사용, 아니면 기본값 사용
    const formattedDates = dates.map((date) => dayjs(date).format('YYYY-MM-DD'));
    const extension = await detectExtension(response.data);
    const fileName = rawFileName
      ? decodeURIComponent(rawFileName)
      : `period_${formattedDates.join('-')}.${extension}`;

    download(response.data, fileName);

    let applicationInfoData = applicationInfo;
    let projectDataData = projectData;
    let meInfo = me;

    if (!applicationInfo) {
      applicationInfoData = await mutateApplicationInfo();
    }

    if (!projectData) {
      const mutatedData = await mutateProjectData();
      projectDataData = mutatedData?.data;
    }

    if (!meInfo) {
      meInfo = await mutateMe();
    }

    const param = {
      channel_id: appId,
      channel_name: applicationInfoData?.data.name,
      use_purpose_type: applicationInfoData?.data.usePurposeType,
      plan_type: applicationInfoData?.data.planType,
      project_id: projectId,
      project_name: projectDataData?.name,
      user_id: meInfo?.id,
      dashboard_type: '플레이 수'
    };

    track.onClick({
      clickEventName: ClickEventName.click_dashboard_button_download,
      params: param
    });
  };

  const days = dayjs(dateRange.endDate).diff(dateRange.startDate, 'day') + 1;
  const isSingleDay = dayjs(dateRange.startDate).isSame(dateRange.endDate, 'day');
  const isMonthly = days >= 60;

  // dailyStatistics가 비어있거나 형식이 맞지 않을 때 dateRange 기반으로 데이터 생성
  const chartData = useMemo(() => {
    const start = dayjs(dateRange.startDate);
    const end = dayjs(dateRange.endDate);
    const serverData = periodData?.dailyStatistics ?? [];

    // 하루인 경우: 시간별 데이터가 필요 (24개 포인트)
    if (isSingleDay) {
      // 서버에서 24시간 데이터가 왔는지 확인
      if (serverData.length >= 24) {
        return serverData;
      }

      // 서버 데이터를 시간대별로 매핑
      const serverDataMap = new Map<number, ProjectDailyStatistics>();
      serverData.forEach((data) => {
        const dataHour = dayjs(data.date).hour();
        // 해당 시간대에 이미 데이터가 있으면 합산
        const existing = serverDataMap.get(dataHour);
        if (existing) {
          serverDataMap.set(dataHour, {
            ...existing,
            playerCount: existing.playerCount + data.playerCount,
            completedPlayerCount: existing.completedPlayerCount + data.completedPlayerCount
          });
        } else {
          serverDataMap.set(dataHour, data);
        }
      });

      // 시간별 데이터 생성 (1시간 간격, 24개 포인트), 서버 데이터가 있으면 사용
      return Array.from({ length: 24 }, (_, hour) => {
        const currentDateTime = start.startOf('day').hour(hour).minute(0).second(0);
        const serverItem = serverDataMap.get(hour);
        return {
          date: currentDateTime.format('YYYY-MM-DDTHH:mm:ss'),
          playerCount: serverItem?.playerCount ?? 0,
          completedPlayerCount: serverItem?.completedPlayerCount ?? 0
        };
      });
    }

    // 두 달 이상인 경우: 월별 데이터가 필요
    if (isMonthly) {
      // 기간 내 월수 계산
      const totalMonths = end.diff(start, 'month') + 1;

      // 서버 데이터가 기간에 맞게 있으면 사용 (월수와 데이터 개수가 같으면)
      if (serverData.length >= totalMonths) {
        return serverData;
      }

      // 서버 데이터를 월별로 매핑
      const serverDataMap = new Map<string, ProjectDailyStatistics>();
      serverData.forEach((data) => {
        const monthKey = dayjs(data.date).format('YYYY-MM');
        // 해당 월에 이미 데이터가 있으면 합산
        const existing = serverDataMap.get(monthKey);
        if (existing) {
          serverDataMap.set(monthKey, {
            ...existing,
            playerCount: existing.playerCount + data.playerCount,
            completedPlayerCount: existing.completedPlayerCount + data.completedPlayerCount
          });
        } else {
          serverDataMap.set(monthKey, data);
        }
      });

      // 월별 데이터 생성, 서버 데이터가 있으면 사용
      const result: ProjectDailyStatistics[] = [];
      let currentDate = start.startOf('month');
      const endDate = end.endOf('month');

      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'month')) {
        const monthKey = currentDate.format('YYYY-MM');
        const serverItem = serverDataMap.get(monthKey);
        result.push({
          date: currentDate.format('YYYY-MM-DD'),
          playerCount: serverItem?.playerCount ?? 0,
          completedPlayerCount: serverItem?.completedPlayerCount ?? 0
        });
        currentDate = currentDate.add(1, 'month');
      }
      return result;
    }

    // 여러 날인 경우: 일별 데이터가 필요
    // 기간 내 일수 계산
    const totalDays = end.diff(start, 'day') + 1;

    // 서버 데이터가 기간에 맞게 있으면 사용 (일수와 데이터 개수가 같으면)
    if (serverData.length >= totalDays) {
      return serverData;
    }

    // 서버 데이터를 날짜별로 매핑
    const serverDataMap = new Map<string, ProjectDailyStatistics>();
    serverData.forEach((data) => {
      const dateKey = dayjs(data.date).format('YYYY-MM-DD');
      serverDataMap.set(dateKey, data);
    });

    // 일별 데이터 생성, 서버 데이터가 있으면 사용
    const result: ProjectDailyStatistics[] = [];
    let currentDate = start.startOf('day');
    const endDate = end.endOf('day');

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      const dateKey = currentDate.format('YYYY-MM-DD');
      const serverItem = serverDataMap.get(dateKey);
      result.push({
        date: dateKey,
        playerCount: serverItem?.playerCount ?? 0,
        completedPlayerCount: serverItem?.completedPlayerCount ?? 0
      });
      currentDate = currentDate.add(1, 'day');
    }
    return result;
  }, [periodData?.dailyStatistics, dateRange, isSingleDay, isMonthly]);

  return (
    <Card cssStyle={CardStyle}>
      <section css={TopSectionLayout}>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <Typography as="h3" type="heading" cssStyle={MarginBottom16}>
            기간별 현황
          </Typography>
          <Button
            icon="download_solid"
            cssStyle={[MarginRight16]}
            type="plain"
            onClick={handleDownload}
            disabled={!canDownload}
          >
            다운로드
          </Button>
        </div>
        <div css={[RowFlex, JustifySpaceBetween, LineBreakpoint]}>
          <div css={[RowFlex, BreakpointMargin]}>
            <div ref={calendarWrapperRef} css={CalendarWrapperStyle}>
              <PeriodSelector
                plan={plan}
                selectedPeriod={selectedPeriod}
                onPeriodChange={handlePeriodChange}
                onDateRangeClick={handleDateRangeClick}
                dateRange={dateRange}
              />
              {showCalendar && (
                <div
                  css={CalendarDropdownStyle}
                  onMouseEnter={handleCalendarMouseEnter}
                  onMouseLeave={handleCalendarClose}
                >
                  <Flatpickr
                    options={{
                      mode: 'range',
                      inline: true,
                      minDate: getMinDateByPlan(plan) ?? undefined,
                      maxDate: 'today',
                      locale: Korean,
                      defaultDate: dates
                    }}
                    value={dates}
                    onChange={([selectedStartDate, selectedEndDate]) => {
                      if (selectedStartDate && selectedEndDate) {
                        const newDateRange = {
                          startDate: dayjs(selectedStartDate).format(),
                          endDate: dayjs(selectedEndDate).format()
                        };

                        setDateRange(newDateRange);
                        setShowCalendar(false);

                        // 부모 컴포넌트에 알림
                        if (onPeriodChange) {
                          onPeriodChange('custom');
                        }

                        if (projectId) {
                          track.onClick({
                            clickEventName: ClickEventName.click_dashboard_input_daterange,
                            params: {
                              projectId
                            }
                          });
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <TrendChart
          labels={chartData.map((daily) => daily.date)}
          playData={chartData.map((daily) => daily.playerCount)}
          completeData={chartData.map((daily) => daily.completedPlayerCount)}
          totalPlayUsers={periodData?.playerCount ?? 0}
          totalCompleteUsers={periodData?.completedPlayerCount ?? 0}
          isSingleDay={isSingleDay}
          isMonthly={isMonthly}
        />
      )}
    </Card>
  );
}

// Mock data 생성 함수: 지정된 기간에 맞춰 일별 또는 시간별 통계 데이터 생성
function generateMockPeriodStatistics(dateRange: {
  startDate: string;
  endDate: string;
}): ProjectPeriodStatisticsResponseModel {
  const start = dayjs(dateRange.startDate);
  const end = dayjs(dateRange.endDate);

  // 같은 날짜인지 확인
  const isSameDay = start.isSame(end, 'day');
  const days = end.diff(start, 'day') + 1;
  const isMonthly = days >= 60;

  const dailyStatistics: ProjectDailyStatistics[] = [];
  let totalPlayerCount = 0;
  let totalCompletedPlayerCount = 0;

  // 하루인 경우 시간별 데이터 생성 (1시간 간격, 24개 포인트)
  if (isSameDay) {
    for (let hour = 0; hour < 24; hour += 1) {
      // startOf('day')를 기준으로 시간 설정
      const currentDateTime = start.startOf('day').hour(hour).minute(0).second(0);

      // 시간대별 플레이 인원 수 생성 (패턴: 오후에 높고 새벽에 낮음)
      const basePlayerCount = 80 + Math.sin((hour / 24) * Math.PI * 2) * 60;
      const playerCount = Math.max(40, Math.round(basePlayerCount + (Math.random() * 30 - 15)));

      // 완료 인원은 플레이 인원의 60~90% 정도로 설정
      const completionRate = 0.6 + Math.random() * 0.3;
      const completedPlayerCount = Math.round(playerCount * completionRate);

      totalPlayerCount += playerCount;
      totalCompletedPlayerCount += completedPlayerCount;

      dailyStatistics.push({
        date: currentDateTime.format('YYYY-MM-DDTHH:mm:ss'),
        playerCount,
        completedPlayerCount
      });
    }
  } else if (isMonthly) {
    // 두 달 이상인 경우 월별 데이터 생성
    let currentDate = start.startOf('month');
    const endDate = end.endOf('month');

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'month')) {
      // 월별 데이터는 일별 데이터보다 규모가 큼 (30배 정도)
      const basePlayerCount = 1500 + Math.random() * 1000;
      const playerCount = Math.round(basePlayerCount);

      const completionRate = 0.6 + Math.random() * 0.3;
      const completedPlayerCount = Math.round(playerCount * completionRate);

      totalPlayerCount += playerCount;
      totalCompletedPlayerCount += completedPlayerCount;

      dailyStatistics.push({
        date: currentDate.format('YYYY-MM-DD'), // 해당 월의 1일로 설정
        playerCount,
        completedPlayerCount
      });

      currentDate = currentDate.add(1, 'month');
    }
  } else {
    // 여러 날인 경우 일별 데이터 생성
    // startDate부터 endDate까지 반복
    let currentDate = start.startOf('day');
    const endDate = end.endOf('day');

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      // 랜덤한 플레이 인원 수 생성 (10~200명 범위, 약간의 변동성 추가)
      const basePlayerCount = 50 + Math.random() * 30;
      const playerCount = Math.max(10, Math.round(basePlayerCount + (Math.random() * 40 - 20)));

      // 완료 인원은 플레이 인원의 60~90% 정도로 설정
      const completionRate = 0.6 + Math.random() * 0.3;
      const completedPlayerCount = Math.round(playerCount * completionRate);

      totalPlayerCount += playerCount;
      totalCompletedPlayerCount += completedPlayerCount;

      dailyStatistics.push({
        date: currentDate.format('YYYY-MM-DD'),
        playerCount,
        completedPlayerCount
      });

      currentDate = currentDate.add(1, 'day');
    }
  }

  return {
    playerCount: totalPlayerCount,
    completedPlayerCount: totalCompletedPlayerCount,
    dailyStatistics
  };
}

function getPeriodDates(period: PeriodOptionValue) {
  const today = dayjs();

  if (period === 'today') {
    return {
      startDate: today.startOf('day').format(),
      endDate: today.endOf('day').format()
    };
  }

  if (period === 'yesterday') {
    const yesterday = today.subtract(1, 'day');
    return {
      startDate: yesterday.startOf('day').format(),
      endDate: yesterday.endOf('day').format()
    };
  }

  const periodMap: Record<string, number> = {
    week: 6,
    month: 29,
    '3months': 89,
    '6months': 179,
    year: 364
  };

  const days = periodMap[period] || 6;

  return {
    startDate: today.subtract(days, 'day').startOf('day').format(),
    endDate: today.endOf('day').format()
  };
}

function getDefaultDateQuery(plan: PlanType = 'Free') {
  let days = 7; // free 기본값

  if (plan === 'Pro' || plan === 'Teacher') {
    days = 30;
  } else if (plan === 'Enterprise' || plan === 'Class' || plan === 'Realworld') {
    days = 365;
  }

  return {
    startDate: dayjs()
      .subtract(days - 1, 'day')
      .startOf('day')
      .format(),
    endDate: dayjs().endOf('day').format()
  };
}

function getMinDateByPlan(plan: PlanType) {
  if (plan === 'Free') {
    return dayjs().subtract(7, 'day').toDate();
  }
  if (plan === 'Pro' || plan === 'Teacher') {
    return dayjs().subtract(30, 'day').toDate();
  }

  if (plan === 'Enterprise' || plan === 'Class') {
    return dayjs().subtract(365, 'day').toDate();
  }

  return null;
}

const CardStyle = css`
  max-width: 1300px;
  min-width: 740px;
  margin-bottom: ${spacing.margin.xlarge2};
`;

const lineBreakpoint = '@media (max-width: 1263px)';

const LineBreakpoint = css`
  ${lineBreakpoint} {
    display: block;
  }
`;

const BreakpointMargin = css`
  ${lineBreakpoint} {
    margin: ${spacing.margin.large} 0;
  }
`;

const CalendarWrapperStyle = css`
  position: relative;
  margin-right: ${spacing.margin.medium};
`;

const CalendarDropdownStyle = css`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1000;
  background: ${color.surface_default_default};
  border-radius: ${spacing.borderRadius.large};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: ${spacing.padding.medium};

  /* Flatpickr inline 모드 스타일 커스터마이징 */
  .flatpickr-calendar {
    box-shadow: none;
    background: transparent;
  }

  input {
    display: none;
  }

  /* 애니메이션 */
  */
  animation: slideDown 200ms ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TopSectionLayout = css`
  > *:last-of-type {
    margin-top: ${spacing.margin.medium};
  }
  margin-bottom: ${spacing.margin.xlarge4};
  align-items: flex-end;
`;
